/**
 * ConfirmModal — generic destructive-action confirmation gate.
 *
 * Extracted from FleetPanel.jsx (Phase 2 P2 decomposition · 2026-06).
 * Behaviour is BYTE-IDENTICAL to the inlined version that shipped in
 * Slice 1 — same testids, same ESC handling, same tone palette.
 */
import React, { useEffect, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";

export const ConfirmModal = ({ title, message, tone = "warn", confirmLabel = "Confirm", onCancel, onConfirm }) => {
  const [busy, setBusy] = useState(false);

  // ESC dismisses — keep parity with SuspendModal + DriverFormModal so
  // keyboard navigation feels consistent across the Fleet area.
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onCancel?.(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onCancel]);

  const handleConfirm = async () => {
    setBusy(true);
    try { await onConfirm?.(); } finally { setBusy(false); }
  };

  const accent = tone === "danger" ? "#EF4444" : "#F59E0B";
  const accentHover = tone === "danger" ? "#DC2626" : "#D97706";

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="confirm-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border-2 border-border p-5 w-full max-w-sm space-y-3"
        data-testid="confirm-modal"
      >
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5" style={{ color: accent }} />
          <h3 className="font-heading text-lg font-black">{title}</h3>
        </div>
        <p className="text-sm text-muted-foreground" data-testid="confirm-modal-message">
          {message}
        </p>
        <div className="flex gap-2 pt-2 border-t border-border">
          <button
            onClick={onCancel}
            disabled={busy}
            className="flex-1 text-xs px-3 py-2 border border-border hover:bg-secondary transition-colors disabled:opacity-60"
            data-testid="confirm-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={busy}
            style={{ backgroundColor: accent }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = accentHover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = accent)}
            className="flex-1 text-xs px-3 py-2 text-white font-bold transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
            data-testid="confirm-modal-confirm"
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
